/**
 * ConversationDetailsApi
 * API service for ConversationDetailsView component
 * Migrated from legacy ConversationApi.js
 */

import * as APIUtils from '@/components/Globals/Utils';
import type { ApiConfig, GetCustomFieldsParams } from '../types';
import type { ConversationDetail } from '@/types/conversation';
import { socketService } from '@/services/socketService';

interface GetConversationByIdParams {
  conversationId: number;
  layersToHide: string;
  getAllReplies: boolean;
  isConversationDetailsView?: boolean;
  totalReplies: number;
  isEnabled: boolean;
  phaseId?: number | null;
}

interface GetConversationsDataParams {
  conversationsIds: string;
}

class ConversationDetailsApi
{
  /**
   * Get conversation by ID
   */
  static getConversationById(
    requestApi: ApiConfig,
    requestParams: GetConversationByIdParams,
  ): Promise<ConversationDetail>
  {
    let urlParams =
      '?conversationId=' +
      requestParams.conversationId +
      '&layersToHide=' +
      requestParams.layersToHide +
      '&getAllReplies=' +
      requestParams.getAllReplies +
      '&isConversationDetailsView=' +
      (requestParams.isConversationDetailsView ? requestParams.isConversationDetailsView : false) +
      '&totalReplies=' +
      requestParams.totalReplies +
      '&isEnabled=' +
      requestParams.isEnabled;

    if (requestParams.phaseId)
    {
      urlParams += '&phaseId=' + requestParams.phaseId;
    }

    return APIUtils.callFetch(
      requestApi,
      'api/Conversation/GetConversationById' + urlParams,
      'POST',
    );
  }

  /**
   * Get conversations data by conversation IDs
   */
  static GetConversationsDataByConversationIds(
    requestApi: ApiConfig,
    requestParams: GetConversationsDataParams,
  ): Promise<Array<{ ConversationId: number; Title: string; ProjectName: string }>>
  {
    const urlParams = '?conversationsIds=' + requestParams.conversationsIds;
    return APIUtils.callFetch(
      requestApi,
      'api/Conversation/GetConversationsDataByConversationIds' + urlParams,
      'POST',
    );
  }

  /**
   * Update complete status
   */
  static updateComplete(
    requestApi: ApiConfig,
    requestParams: {
      DashboardGroupId?: number;
      ConversationId: number;
      Finished: boolean;
      SocketConnectionId?: string;
    },
  ): Promise<boolean>
  {
    if (requestParams.DashboardGroupId && requestParams.DashboardGroupId > 0)
    {
      const urlParams =
        '?DashboardGroupId=' +
        requestParams.DashboardGroupId +
        '&ConversationId=' +
        requestParams.ConversationId +
        '&Finished=' +
        requestParams.Finished;

      return APIUtils.callFetch(
        requestApi,
        'api/conversation/UpdateCompleteDashboardGroupConversation' + urlParams,
        'POST',
      );
    }
    else
    {
      const urlParams =
        '?ConversationId=' +
        requestParams.ConversationId +
        '&Finished=' +
        requestParams.Finished +
        '&SocketConnectionId=' +
        requestParams.SocketConnectionId;

      return APIUtils.callFetch(
        requestApi,
        'api/conversation/UpdateCompleteConversation' + urlParams,
        'PUT',
      );
    }
  }

  /**
   * Update conversation title
   */
  static updateTitle(requestApi: ApiConfig, requestParams: unknown): Promise<ConversationDetail>
  {
    return APIUtils.callFetch(
      requestApi,
      'api/conversation/updateconversationtitle',
      'PUT',
      { ...(requestParams as any), SocketConnectionId: socketService.connectionId },
    );
  }

  /**
   * Update red flags
   */
  static updateRedFlags(requestApi: ApiConfig, requestParams: unknown): Promise<void>
  {
    return APIUtils.callFetch(
      requestApi,
      'api/conversation/MarkRedFlag',
      'PUT',
      { ...(requestParams as any), SocketConnectionId: socketService.connectionId },
      true,
    );
  }

  /**
   * Get tag list
   */
  static getTagList(
    requestApi: ApiConfig,
    requestParams: {
      ConfigValueId: number;
      ProjectId: number;
      LayerIdsSelected: string;
      GroupIdsSelected: string;
      ConversationId: number;
    },
  ): Promise<unknown>
  {
    const urlParams =
      '?configValueId=' +
      requestParams.ConfigValueId +
      '&projectId=' +
      requestParams.ProjectId +
      '&layerIdsSelected=' +
      requestParams.LayerIdsSelected +
      '&groupIdsSelected=' +
      requestParams.GroupIdsSelected +
      '&conversationId=' +
      requestParams.ConversationId;

    return APIUtils.callFetch(requestApi, 'api/Person/getTagList' + urlParams, 'GET');
  }

  /**
   * Update conversation body
   */
  static updateBody(requestApi: ApiConfig, requestParams: unknown): Promise<ConversationDetail>
  {
    return APIUtils.callFetch(
      requestApi,
      'api/conversation/updateconversationbody',
      'PUT',
      requestParams,
    );
  }

  /**
   * Get more replies
   */
  static getMoreReplies(
    requestApi: ApiConfig,
    requestParams: {
      ConversationId: number;
      LayersToHide: string;
      RecycleBinDashboard: boolean;
      pageNumber: number;
      pageSize: number;
      isConversationDetailsView: boolean;
    },
  ): Promise<unknown>
  {
    const urlParams =
      '?conversationId=' +
      requestParams.ConversationId +
      '&layersToHide=' +
      requestParams.LayersToHide +
      '&recycleBinDashboard=' +
      requestParams.RecycleBinDashboard +
      '&pageNumber=' +
      requestParams.pageNumber +
      '&pageSize=' +
      requestParams.pageSize +
      '&isConversationDetailsView=' +
      requestParams.isConversationDetailsView;

    return APIUtils.callFetch(
      requestApi,
      'api/reply/GetMoreConversationReplies' + urlParams,
      'GET',
    );
  }

  /**
   * Save follower
   */
  static saveFollower(requestApi: ApiConfig, requestParams: unknown): Promise<void>
  {
    return APIUtils.callFetch(
      requestApi,
      'api/Notification/SaveConversationsFollower',
      'POST',
      requestParams,
      true,
    );
  }

  /**
   * Get custom fields by item ID
   */
  static getCustomFieldsByItemId(
    requestApi: ApiConfig,
    requestParams: GetCustomFieldsParams,
  ): Promise<unknown>
  {
    return APIUtils.callFetch(
      requestApi,
      'api/CustomField/GetCustomFieldsByItemId?CoreTaskId=' +
        requestParams.coreTaskId +
        '&IsDeleted=' +
        requestParams.isDeleted +
        '&ProjectId=' +
        requestParams.projectId,
    );
  }
}

export default ConversationDetailsApi;
